import { type ReactNode } from "react";
import { Link, router } from "@inertiajs/react";
import {
  Target,
  CalendarDays,
  Sprout,
  Receipt,
  Activity as ActivityIcon,
  Infinity as InfinityIcon,
  ArrowUpRight,
} from "lucide-react";
import { DetailTemplate } from "@/templates/DetailTemplate";
import { ImpactGrid, ImpactTile } from "@/portal/templates/ImpactDashboardTemplate";
import { Card } from "@/components/Card";
import { Menu } from "@/components/Menu";
import { Button } from "@/components/Button";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LineChart } from "@/charts/LineChart";

interface GoalMeta {
  id: string;
  objId: string;
  name: string;
  description: string | null;
  projectId: string | null;
  projectName: string | null;
  targetMinor: number;
  infiniteQuantity: boolean;
  quantityAvailable: number | null;
  quantityRemaining: number | null;
  createdAt: string | null;
  lifetimeAllocatedMinor: number;
  lifetimeContributions: number;
  progressPct: number | null;
}

interface Overview {
  days: number;
  totalAllocatedMinor: number;
  deltaRaisedPct: number;
  sparkRaised: number[];
  totalContributions: number;
  deltaContributionsPct: number;
  sparkContributions: number[];
  contributors: number;
  deltaContributorsPct: number;
  sparkContributors: number[];
  labels: string[];
  amountByDay: number[];
  contributionsByDay: number[];
  contributorsByDay: number[];
  recentAllocations: {
    amountMinor: number;
    sourceName: string | null;
    status: string;
    when: string | null;
  }[];
}

interface Props {
  goal: GoalMeta;
  overview: Overview;
}

const RANGES = [
  { id: 7, label: "Last 7 days" },
  { id: 30, label: "Last 30 days" },
  { id: 90, label: "Last 90 days" },
  { id: 365, label: "Last 12 months" },
];

function fmtMoney(minor: number, compact = false) {
  const major = minor / 100;
  if (compact && Math.abs(major) >= 1000)
    return `£${(major / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return `£${major.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Exact amount (always two decimals) — surfaces pence on individual rows.
function fmtMoneyExact(minor: number) {
  return `£${(minor / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export default function GoalsShow({ goal, overview }: Props) {
  const rangeLabel = RANGES.find((r) => r.id === overview.days)?.label ?? `Last ${overview.days} days`;
  const rangeLower = rangeLabel.toLowerCase();

  function setRange(days: number) {
    router.get(window.location.pathname, { days }, { only: ["overview"], preserveScroll: true, preserveState: true });
  }

  return (
    <DetailTemplate
      title={goal.name}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          {goal.projectName &&
            (goal.projectId ? (
              <Link
                href={`/projects/${goal.projectId}`}
                className="text-fg-muted hover:text-accent-text transition-colors"
              >
                {goal.projectName}
              </Link>
            ) : (
              <span className="text-fg-muted">{goal.projectName}</span>
            ))}
          <span className="font-mono text-xs text-fg-subtle">{goal.objId}</span>
        </span>
      }
      backTo="/goals"
      backLabel="All goals"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Goals", to: "/goals" }, { label: goal.name }]}
      leading={
        <span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-5">
          <Target />
        </span>
      }
      primaryAction={
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button intent="secondary" size="sm">
              <CalendarDays /> {rangeLabel}
            </Button>
          </Menu.Trigger>
          <Menu.Content align="end">
            <Menu.Label>Time range</Menu.Label>
            {RANGES.map((r) => (
              <Menu.CheckboxItem
                key={r.id}
                checked={overview.days === r.id}
                onCheckedChange={() => setRange(r.id)}
              >
                {r.label}
              </Menu.CheckboxItem>
            ))}
          </Menu.Content>
        </Menu.Root>
      }
    >
      <ImpactGrid>
        <ImpactTile col={3} index={0}>
          <StatCard
            label="Allocated"
            value={fmtMoney(overview.totalAllocatedMinor, true)}
            delta={overview.deltaRaisedPct}
            trend={overview.sparkRaised}
            hint={`vs prior ${rangeLower}`}
          />
        </ImpactTile>
        <ImpactTile col={3} index={1}>
          <StatCard
            label="Contributions"
            value={overview.totalContributions.toLocaleString()}
            delta={overview.deltaContributionsPct}
            trend={overview.sparkContributions}
            hint={rangeLower}
          />
        </ImpactTile>
        <ImpactTile col={3} index={2}>
          <StatCard
            label="Contributors"
            value={overview.contributors.toLocaleString()}
            delta={overview.deltaContributorsPct}
            trend={overview.sparkContributors}
            hint={rangeLower}
          />
        </ImpactTile>
        <ImpactTile col={3} index={3}>
          <StatCard
            label="Progress"
            value={goal.progressPct == null ? "Open-ended" : `${goal.progressPct}%`}
            hint={goal.targetMinor > 0 ? `of ${fmtMoney(goal.targetMinor, true)} target` : "no fixed target"}
          />
        </ImpactTile>

        {/* Contributions over time */}
        <ImpactTile col={8} index={4}>
          <Card className="h-full">
            <Card.Header
              title="Contributions over time"
              description={`Daily contributions, contributors and amount to this goal, ${rangeLower}.`}
            />
            <Card.Body>
              {overview.contributionsByDay.every((v) => v === 0) ? (
                <MiniEmpty icon={<ActivityIcon />} text="No contributions in this period." />
              ) : (
                <LineChart
                  labels={overview.labels}
                  series={[
                    { label: "Contributions", data: overview.contributionsByDay, focal: true },
                    { label: "Contributors", data: overview.contributorsByDay },
                    { label: "Amount", data: overview.amountByDay, axis: "right" },
                  ]}
                  formatY={(v) => v.toLocaleString()}
                  formatRightY={(v) => fmtMoney(v, true)}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* Goal info */}
        <ImpactTile col={4} index={5}>
          <Card className="h-full">
            <Card.Header title="Goal info" />
            <Card.Body className="p-0">
              <dl className="divide-y divide-border-subtle text-sm">
                <InfoRow label="Project">
                  {goal.projectId ? (
                    <Link
                      href={`/projects/${goal.projectId}`}
                      className="inline-flex items-center gap-1 text-accent-text hover:underline [&_svg]:size-3"
                    >
                      <Sprout /> {goal.projectName}
                    </Link>
                  ) : (
                    goal.projectName ?? "—"
                  )}
                </InfoRow>
                <InfoRow label="Target">
                  <span className="tabular">{goal.targetMinor > 0 ? fmtMoney(goal.targetMinor) : "—"}</span>
                </InfoRow>
                <InfoRow label="Quantity">
                  {goal.infiniteQuantity ? (
                    <span className="inline-flex items-center gap-1 [&_svg]:size-3.5">
                      <InfinityIcon /> Unlimited
                    </span>
                  ) : goal.quantityAvailable != null ? (
                    <span className="tabular">
                      {(goal.quantityRemaining ?? 0).toLocaleString()} of{" "}
                      {goal.quantityAvailable.toLocaleString()} left
                    </span>
                  ) : (
                    "—"
                  )}
                </InfoRow>
                {goal.createdAt && (
                  <InfoRow label="Added">
                    <span className="tabular">
                      {new Date(goal.createdAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </InfoRow>
                )}
              </dl>
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* Recent allocations */}
        <ImpactTile col={12} index={6}>
          <Card>
            <Card.Header
              title="Recent allocations"
              description="Latest contributions allocated to this goal."
              action={
                overview.recentAllocations.length > 0 ? (
                  <Button asChild intent="link" size="sm">
                    <Link href={`/goals/${goal.id}/allocations`}>
                      View all <ArrowUpRight />
                    </Link>
                  </Button>
                ) : undefined
              }
            />
            <Card.Body className={overview.recentAllocations.length === 0 ? "" : "p-0"}>
              {overview.recentAllocations.length === 0 ? (
                <MiniEmpty icon={<Receipt />} text="No allocations yet." />
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {overview.recentAllocations.map((a, i) => (
                    <li key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent-text [&_svg]:size-3.5">
                        <Receipt />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-fg">{a.sourceName ?? "—"}</div>
                        <div className="truncate text-xs text-fg-subtle">{fmtRelative(a.when)}</div>
                      </div>
                      <StatusBadge status={a.status} hideIcon />
                      <span className="shrink-0 tabular text-fg">{fmtMoneyExact(a.amountMinor)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        </ImpactTile>
      </ImpactGrid>

      {/* Lifetime footer */}
      <Card className="mt-3">
        <Card.Body className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-fg-muted">
          <span className="tabular">{goal.lifetimeContributions.toLocaleString()} contributions all-time</span>
          <span className="tabular">{fmtMoney(goal.lifetimeAllocatedMinor, true)} allocated all-time</span>
          {goal.progressPct != null && <span className="tabular">{goal.progressPct}% of target</span>}
        </Card.Body>
      </Card>
    </DetailTemplate>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-4 py-2.5">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="min-w-0 truncate text-right text-fg">{children}</dd>
    </div>
  );
}

function MiniEmpty({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <div className="flex size-9 items-center justify-center rounded-full bg-muted text-fg-muted [&_svg]:size-4">
        {icon}
      </div>
      <p className="text-xs text-fg-muted">{text}</p>
    </div>
  );
}
